/**
 * Page Access Token Retrieval (Story 9.2, Task 1.8)
 *
 * Retrieves and decrypts a Facebook Page Access Token from the KV blob.
 * Page tokens are stored per-page during OAuth discovery (Story 9.1).
 *
 * CRITICAL: The Page Access Token is used for Page Insights API calls,
 * NOT the user's long-lived token. Using the wrong token returns
 * "permission denied" or empty data.
 *
 * Token KV blob structure (from Story 9.1):
 * {
 *   accessTokenEncrypted: string,
 *   refreshTokenEncrypted?: string,
 *   scopes: string[],
 *   connectingUserId: string,
 *   expiresAt: number,
 *   pageTokens: Record<pageId, { encrypted: string, pageName: string }>
 * }
 */

import type { AdapterResult } from "../adapter";
import { adapterOk, adapterErr } from "../adapter";

// ============================================================================
// Types
// ============================================================================

interface SelectedPageToken {
  readonly encrypted: string;
  readonly connectingUserId: string;
}

export interface PageTokenResult {
  pageAccessToken: string;
  connectingUserId: string;
}

/**
 * Least-privilege credential exposed to the Facebook Pages adapter while a
 * live-evidence certification is running. The server owns the KV lookup and
 * decryption inputs; the adapter receives only the already selected Page
 * Access Token through a zero-argument, closure-bound capability.
 */
export interface CertificationPageAccessToken {
  readonly pageAccessToken: string;
}

export type CertificationPageAccessTokenResolver = () => Promise<
  AdapterResult<CertificationPageAccessToken>
>;

const CERTIFICATION_PAGE_TOKEN_OWNER_MISMATCH = {
  code: "token_invalid",
  message: "Certification page token ownership validation failed",
  retryable: false,
} as const;

const INVALID_KV_PAGE_TOKEN_DATA = {
  code: "invalid_response",
  message: "OAuth page token data in KV is invalid",
  retryable: false,
} as const;

// ============================================================================
// Helper
// ============================================================================

async function selectPageToken(
  kv: unknown,
  oauthTokenId: string,
  pageId: string
): Promise<AdapterResult<SelectedPageToken>> {
  // Load KV blob
  const kvStore = kv as { get(key: string): Promise<string | null> };
  const raw = await kvStore.get(`oauth_token:${oauthTokenId}`);
  if (!raw) {
    return adapterErr({
      code: "token_expired",
      message: "OAuth token not found in KV — reconnect the integration",
      retryable: false,
    });
  }

  let tokenData: unknown;
  try {
    tokenData = JSON.parse(raw) as unknown;
  } catch {
    return adapterErr(INVALID_KV_PAGE_TOKEN_DATA);
  }

  if (
    typeof tokenData !== "object" ||
    tokenData === null ||
    Array.isArray(tokenData)
  ) {
    return adapterErr(INVALID_KV_PAGE_TOKEN_DATA);
  }

  const connectingUserId = Reflect.get(tokenData, "connectingUserId");
  if (
    typeof connectingUserId !== "string" ||
    connectingUserId.trim().length === 0
  ) {
    return adapterErr(INVALID_KV_PAGE_TOKEN_DATA);
  }

  const pageTokens = Reflect.get(tokenData, "pageTokens");
  // pageTokens may be absent if OAuth discovery timed out. Preserve the
  // established reconnect path for that valid-but-incomplete blob state.
  if (pageTokens === undefined) {
    return adapterErr({
      code: "account_not_found",
      message: "Page token not found — re-run account discovery",
      retryable: false,
    });
  }
  if (
    typeof pageTokens !== "object" ||
    pageTokens === null ||
    Array.isArray(pageTokens)
  ) {
    return adapterErr(INVALID_KV_PAGE_TOKEN_DATA);
  }

  if (!Object.prototype.hasOwnProperty.call(pageTokens, pageId)) {
    return adapterErr({
      code: "account_not_found",
      message: "Page token not found — re-run account discovery",
      retryable: false,
    });
  }

  const pageToken = Reflect.get(pageTokens, pageId);
  if (
    typeof pageToken !== "object" ||
    pageToken === null ||
    Array.isArray(pageToken)
  ) {
    return adapterErr(INVALID_KV_PAGE_TOKEN_DATA);
  }

  const encrypted = Reflect.get(pageToken, "encrypted");
  const pageName = Reflect.get(pageToken, "pageName");
  if (
    typeof encrypted !== "string" ||
    encrypted.trim().length === 0 ||
    typeof pageName !== "string"
  ) {
    return adapterErr(INVALID_KV_PAGE_TOKEN_DATA);
  }

  return adapterOk({
    encrypted,
    connectingUserId,
  });
}

async function decryptPageToken(
  encrypted: string,
  tenantId: string,
  userId: string,
  encryptionKey: string
): Promise<string> {
  // Dynamic import to avoid pulling WASM deps into frontend bundles
  const { decryptToken } = await import("@provenleap/auth");
  return decryptToken(encrypted, tenantId, userId, {
    ENCRYPTION_KEY: encryptionKey,
  });
}

/**
 * Retrieves and decrypts the Page Access Token for a specific Facebook Page.
 *
 * @param kv - Cloudflare KV binding
 * @param oauthTokenId - The OAuth token blob ID from the integration row
 * @param pageId - The Facebook Page ID (accountId from integration row)
 * @param tenantId - Tenant ID for HKDF key derivation
 * @param encryptionKey - Server encryption key
 * @returns AdapterResult<PageTokenResult> with the decrypted Page Access Token
 */
export async function getPageAccessToken(
  kv: unknown,
  oauthTokenId: string,
  pageId: string,
  tenantId: string,
  encryptionKey: string
): Promise<AdapterResult<PageTokenResult>> {
  const selected = await selectPageToken(kv, oauthTokenId, pageId);
  if (!selected.success) {
    return adapterErr(selected.error);
  }

  // Decrypt with connectingUserId FROM KV BLOB (NOT requesting user's ID!)
  const pageAccessToken = await decryptPageToken(
    selected.data.encrypted,
    tenantId,
    selected.data.connectingUserId,
    encryptionKey
  );

  return adapterOk({
    pageAccessToken,
    connectingUserId: selected.data.connectingUserId,
  });
}

/**
 * Server-side certification helper that deliberately strips token-row
 * metadata from the result. Callers bind all six inputs in a closure and
 * expose only that zero-argument closure to the adapter. Unlike the normal
 * product helper, certification requires the KV owner to match the selected
 * D1 token-row owner before attempting decryption.
 */
export async function getCertificationPageAccessToken(
  kv: unknown,
  oauthTokenId: string,
  pageId: string,
  tenantId: string,
  expectedTokenUserId: string,
  encryptionKey: string
): Promise<AdapterResult<CertificationPageAccessToken>> {
  const selected = await selectPageToken(kv, oauthTokenId, pageId);
  if (!selected.success) {
    return adapterErr(selected.error);
  }
  if (
    expectedTokenUserId.length === 0 ||
    selected.data.connectingUserId !== expectedTokenUserId
  ) {
    return adapterErr(CERTIFICATION_PAGE_TOKEN_OWNER_MISMATCH);
  }

  const pageAccessToken = await decryptPageToken(
    selected.data.encrypted,
    tenantId,
    expectedTokenUserId,
    encryptionKey
  );
  return adapterOk({ pageAccessToken });
}
